import { createTheme, responsiveFontSizes } from "@mui/material/styles";

const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1024,
  xl: 1215,
};

// Create a theme instance.
let theme = createTheme({
  breakpoints: {
    values: breakpoints,
  },
  palette: {
    primary: {
      main: "#035C67",
      light: "#009D8C",
    },
    secondary: {
      main: "#1e0258",
    },
    success: {
      main: "#ffffff",
    },
    error: {
      main: "#AB0033",
    },
    common: {
      white: "#fff",
      black: "#000",
    },
  },
  typography: {
    fontSize: 16,
    body1: {
      fontSize: "1rem",
    },
    h1: {
      fontSize: "1.75rem",
      color: "#1e0258",
      fontWeight: "700",
      position: "relative",
      letterSpacing: "inherit",
      "&:after": {
        content: '""',
        background: "#23085A",
        width: "58px",
        height: "4px",
        borderRadius: "5px",
        position: "absolute",
        left: 0,
        bottom: "-8px",
      },
    },
  },
});

// styles overrides
theme = createTheme(theme, {
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.secondary.main,
          borderRadius: 0,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          background: theme.palette.secondary.main,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 3,
      },
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "&.MuiPaper-elevation3": {
            border: "1px solid #C8C8C8",
            boxShadow: "0px 2px 8px 0px rgba(84, 86, 90, 0.24)",
          },
          "&.green": {
            border: "1px solid rgba(3, 92, 103, 0.25)",
            background: " #F0FCF8",
          },
          "&.grey": {
            background: "#F4F4F4",
          },
          "&.lightGrey": {
            background: "#FAFAFA",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          outline: "none",
          fontWeight: "700",
          padding: "12px 20px",
          borderRadius: "8px",
          textTransform: "none",
          lineHeight: "1.5",
          opacity: "0.8",
          "&:hover": {
            opacity: "1",
          },
          "&.MuiButton-outlined": {
            borderWidth: "2px",
          },
          "&.MuiButton-outlinedPrimary": {
            borderColor: theme.palette.primary.main,
          },
          "& .MuiSvgIcon-root, & .MuiButton-startIcon .MuiSvgIcon-root": {
            fontSize: "1rem",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "&& .MuiOutlinedInput-notchedOutline, &&:hover .MuiOutlinedInput-notchedOutline, &&.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderColor: "#C8C8C8",
              borderWidth: "1px",
            },
          "&&.Mui-error:hover .MuiOutlinedInput-notchedOutline, &&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderColor: theme.palette.error.main,
            },
        },
        input: {
          height: "48px",
          padding: "0px 15px!important",
          "&::placeholder": {
            fontSize: "14px",
          },
          "&::-webkit-outer-spin-button,::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
          "&[type=number] ": {
            " -moz-appearance": "textfield",
          },
          ".Mui-error > &": {
            color: theme.palette.error.main,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          "&.Mui-error": {
            borderLeft: "3px solid #ab0033",
            paddingLeft: "10px",
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          height: "44px",
          width: "44px",
          border: "1px solid #898a8d",
          flexShrink: 0,
          color: "#898a8d",
          svg: {
            height: "16px",
            width: "16px",
          },
          ".MuiFormControlLabel-root:has(> &)": {
            marginLeft: 0,
            gap: "16px",
          },
          "&.Mui-checked": {
            border: "1px solid #005CB9",
            color: "#005CB9",
            background:
              "linear-gradient(0deg, #005CB91A, #005CB91A),linear-gradient(0deg, rgba(0, 92, 185, 0.1), rgba(0, 92, 162, 0.1)),linear-gradient(0deg, #FFFFFF, #FFFFFF)",
            svg: {
              "&:nth-of-type(2)": {
                transform: "scale(1.4)",
              },
            },
          },
          "& + .MuiTypography-root": {
            fontSize: "14px",
            color: "#333",
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          "& *": {
            "&::-webkit-scrollbar ": {
              width: "8px",
              height: "8px",
            },

            "&::-webkit-scrollbar-track": {
              borderRadius: "4px",
              backgroundColor: "#F0FCF8",
            },

            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#035C67",
              borderRadius: "4px",
            },
          },
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme, {
  variants: [
    //"h1", // fixed size for h1
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "subtitle1",
    "subtitle2",
    "body1",
    "body2",
    "caption",
    "button",
    "overline",
  ],
});

export default theme;
